import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import type {
  AdPlatform,
  BrandProfileInput,
  AdAnalysisSummary,
  GetAdAccountsResponse,
  GetCompetitorsResponse,
  PutAdAccountsRequest,
  PutCompetitorsRequest,
  SessionSnapshot,
  StepState,
} from '@/contracts/onboarding-session';
import { HttpError } from '@/features/onboarding/services/http-client';

import type { DataState } from '../state/buildTranscript';
import { isTerminalState } from '../state/jobState';
import {
  getAdAccounts,
  getAdAnalysis,
  getBrandGuide,
  getCompetitors,
  postQuiz,
  putAdAccounts,
  putCompetitors,
} from '../services/onboarding-session.service';
import { onboardingKeys } from './queryKeys';
import { withRecovery, type RecoverSession } from './useSessionRecovery';

/** Slow re-read while something upstream is still working (the snapshot poll drives the fast one). */
const SETTLING_POLL_MS = 3000;
/** Slow re-read of an ad analysis that is `partial` but not `final`: a late profile can still make it `done`. */
const PARTIAL_ANALYSIS_POLL_MS = 15_000;

/** The BFF's 400 for an ad account or competitor id that is not in what it discovered. */
function isUnknownId(error: unknown): boolean {
  const code = error instanceof HttpError ? error.envelope?.error?.code : undefined;
  return code === 'unknown_ad_account' || code === 'unknown_competitor';
}

/**
 * A read that failed is final for the flow: the transcript leaves that step out, so a late success (a poll, a
 * refocus, a reconnect) must not bring the step back. The settling poll and the window refetches stop once the
 * query is in error with nothing to show (a failed refresh on top of data keeps going); a remount or an explicit invalidation (a new BFF session instance) still re-reads.
 */
const notAfterError = (query: { state: { status: string; data: unknown } }): boolean =>
  !(query.state.status === 'error' && query.state.data === undefined);

/** Maps a query to the transcript's DataState. */
function toDataState<T>(q: { data: T | undefined; isError: boolean; isPending: boolean; fetchStatus: string }): DataState<T> {
  if (q.data !== undefined) return { state: 'ready', data: q.data };
  if (q.isError) return { state: 'error' };
  return q.isPending && q.fetchStatus === 'idle' ? { state: 'idle' } : { state: 'loading' };
}

/**
 * GET /brand-guide, re-read while the site or research job is still working, and once more when the last of them
 * ends: the poll stops right there, and its last read may predate what that job found.
 */
export function useBrandGuide(sessionId: string, enabled: boolean, snapshot: SessionSnapshot | null, recover?: RecoverSession) {
  const settling = (snapshot?.jobs ?? []).some(
    (j) => (j.kind === 'siteProfile' || j.kind === 'research') && !isTerminalState(j.state),
  );
  const query = useQuery({
    queryKey: onboardingKeys.brandGuide(sessionId),
    queryFn: () => withRecovery(() => getBrandGuide(sessionId), recover),
    enabled,
    retry: 1,
    refetchInterval: settling ? SETTLING_POLL_MS : false,
  });
  const { refetch } = query;
  const wasSettling = useRef(settling);
  useEffect(() => {
    if (wasSettling.current && !settling) void refetch();
    wasSettling.current = settling;
  }, [settling, refetch]);
  return query;
}

function platformsActive(data: GetAdAccountsResponse | undefined): boolean {
  return !data || data.platforms.some((p) => p.status !== 'unsupported' && !isTerminalState(p.status as StepState));
}

/** GET /ad-accounts, re-read while a platform is still discovering ads. */
export function useAdAccounts(sessionId: string, enabled: boolean, recover?: RecoverSession): DataState<GetAdAccountsResponse> {
  const q = useQuery({
    queryKey: onboardingKeys.adAccounts(sessionId),
    queryFn: () => withRecovery(() => getAdAccounts(sessionId), recover),
    enabled,
    retry: 1,
    refetchInterval: (query) => (notAfterError(query) && platformsActive(query.state.data) ? SETTLING_POLL_MS : false),
    refetchOnWindowFocus: notAfterError,
    refetchOnReconnect: notAfterError,
  });
  return toDataState(q);
}

/** How long to wait before re-reading an analysis, or false when the BFF will not move the job any more. */
function analysisPollMs(data: AdAnalysisSummary | undefined): number | false {
  if (!data) return SETTLING_POLL_MS;
  if (data.final) return false;
  return data.state === 'partial' ? PARTIAL_ANALYSIS_POLL_MS : SETTLING_POLL_MS;
}

/**
 * GET /ads/:platform/analysis, re-read every few seconds while the analysis is `queued` (ads still being found)
 * or `running`, every `PARTIAL_ANALYSIS_POLL_MS` while it is `partial` and not yet `final`, and stopped once the
 * BFF says it is `final`. The route also moves the BFF's analysis job, so this poll is what advances it. A failed read with nothing to show is final for the card (it
 * says the analysis is unavailable); a failed refresh on top of data keeps going.
 */
export function useAdAnalysis(
  sessionId: string,
  platform: AdPlatform,
  enabled: boolean,
  recover?: RecoverSession,
): DataState<AdAnalysisSummary> {
  const q = useQuery({
    queryKey: onboardingKeys.adAnalysis(sessionId, platform),
    queryFn: () => withRecovery(() => getAdAnalysis(sessionId, platform), recover),
    enabled,
    retry: 1,
    refetchInterval: (query) => (notAfterError(query) ? analysisPollMs(query.state.data) : false),
    refetchOnWindowFocus: notAfterError,
    refetchOnReconnect: notAfterError,
  });
  return toDataState(q);
}

/**
 * The ad analysis as the cache holds it, with no request of its own: the ads card owns the poll (and only mounts
 * once the ad step is reached, so nothing here starts the BFF's analysis early). The Tasks panel reads its
 * `coverage` from here.
 */
export function useCachedAdAnalysis(sessionId: string, platform: AdPlatform): DataState<AdAnalysisSummary> {
  const q = useQuery<AdAnalysisSummary>({
    queryKey: onboardingKeys.adAnalysis(sessionId, platform),
    queryFn: skipToken,
  });
  return toDataState(q);
}

/** GET /competitors, re-read while the research workflow has not produced a shortlist. */
export function useCompetitors(sessionId: string, enabled: boolean, recover?: RecoverSession): DataState<GetCompetitorsResponse> {
  const q = useQuery({
    queryKey: onboardingKeys.competitors(sessionId),
    queryFn: () => withRecovery(() => getCompetitors(sessionId), recover),
    enabled,
    retry: 1,
    refetchInterval: (query) => {
      if (!notAfterError(query)) return false;
      const data = query.state.data;
      return !data || (!isTerminalState(data.status) && data.competitors.length === 0) ? SETTLING_POLL_MS : false;
    },
    refetchOnWindowFocus: notAfterError,
    refetchOnReconnect: notAfterError,
  });
  return toDataState(q);
}

/**
 * POST /quiz at quiz-done. The BFF side is idempotent, so a repeat is harmless. No retry of its own: a failure
 * is shown and re-armed with backoff by `useQuizSubmit`; a lost session is recovered and retried once here.
 */
export function usePostQuiz(sessionId: string, recover?: RecoverSession) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (answers: BrandProfileInput) => withRecovery(() => postQuiz(sessionId, answers), recover),
    onSuccess: () => {
      client.setQueryData<SessionSnapshot>(onboardingKeys.snapshot(sessionId), (old) =>
        old ? { ...old, quizSubmitted: true } : old,
      );
    },
  });
}

/** PUT /ad-accounts/:platform; the BFF stores the confirmation and the snapshot picks it up. */
export function useConfirmAdAccounts(sessionId: string, recover?: RecoverSession) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ platform, body }: { platform: AdPlatform; body: PutAdAccountsRequest }) =>
      withRecovery(() => putAdAccounts(sessionId, platform, body), recover),
    // An id the BFF does not know (the list moved on): re-read the list so the panel offers what exists.
    onError: (error) => {
      if (isUnknownId(error)) void client.invalidateQueries({ queryKey: onboardingKeys.adAccounts(sessionId) });
    },
    onSuccess: (result) => {
      client.setQueryData<SessionSnapshot>(onboardingKeys.snapshot(sessionId), (old) =>
        old
          ? {
              ...old,
              confirmations: {
                ...old.confirmations,
                adAccounts: { ...old.confirmations.adAccounts, [result.platform]: result.confirmation },
              },
            }
          : old,
      );
      void client.invalidateQueries({ queryKey: onboardingKeys.adAccounts(sessionId) });
      // The BFF now follows the confirmed account: its analysis and the live-ad count (in the snapshot) moved.
      void client.invalidateQueries({ queryKey: onboardingKeys.adAnalysis(sessionId, result.platform) });
      void client.invalidateQueries({ queryKey: onboardingKeys.snapshot(sessionId) });
    },
  });
}

/** PUT /competitors. */
export function useConfirmCompetitors(sessionId: string, recover?: RecoverSession) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PutCompetitorsRequest) => withRecovery(() => putCompetitors(sessionId, body), recover),
    onError: (error) => {
      if (isUnknownId(error)) void client.invalidateQueries({ queryKey: onboardingKeys.competitors(sessionId) });
    },
    onSuccess: (result) => {
      client.setQueryData<SessionSnapshot>(onboardingKeys.snapshot(sessionId), (old) =>
        old ? { ...old, confirmations: { ...old.confirmations, competitors: result.confirmation } } : old,
      );
      void client.invalidateQueries({ queryKey: onboardingKeys.competitors(sessionId) });
    },
  });
}
